import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Button,
    Card,
    Collapse,
    Descriptions,
    List,
    Modal,
    Space,
    Spin,
    Tag,
    Timeline,
    Tooltip,
    Typography
} from "antd";
import {useRequest} from "ahooks";
import {CardProps} from "antd/lib/card";
import npiApi from "../../services/api";
import {DevCardTitle} from "./dev-dashboard";
import moment from "moment";
import {isRunningImport, ITagLog, ITagLogStep} from '../../types/tag';
import {FileExcelOutlined} from "@ant-design/icons/lib";
import {ReloadOutlined, UserDeleteOutlined, UserSwitchOutlined} from "@ant-design/icons";


const mapColor = {
    STARTED: "blue",
    PENDING: "orange",
    COMPLETED: "green",
    FAILED: "red",
}
export const TagLogStatus = ({ status, onClick, disabled }: { status: string, onClick?: () => void, disabled?: boolean}) => {
    return <Tag color={disabled ? "lightgray" : mapColor[status]} onClick={onClick}>
        {status}
    </Tag>
}

const JsonBlock: React.FC<{ data: unknown, whiteSpace?:number }> = ({ data, whiteSpace=2 }) => (
    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
    {JSON.stringify(data, null, whiteSpace)}
  </pre>
);

const PrettyBlock: React.FC<{ data: unknown }> = ({ data }) => {
    const entries: [string, any][] = Array.isArray(data)
        ? data.map((v, i) => [String(i), v])
        : typeof data === "object" && data !== null
            ? Object.entries(data)
            : [["value", data]];

    return <Space direction={"vertical"} size={0} style={{marginLeft: 8}}>
        {entries.map(([k, v]) => (
            <Space key={k}>
                <span style={{fontWeight: 600}}>{k} : </span>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {JSON.stringify(v, null, 1)}
                </pre>
            </Space>
        ))}
    </Space>

};

function formatDuration(ms, precision = 2) {
    if (ms < 0) ms = Math.abs(ms);

    const units = [
        { label: "h",  value: Math.floor(ms / 3_600_000) },
        { label: "m",  value: Math.floor((ms % 3_600_000) / 60_000) },
        { label: "s",  value: Math.floor((ms % 60_000) / 1_000) },
        { label: "ms", value: ms % 1_000 },
    ];

    const parts = units
        .filter(u => u.value > 0)      // drop zero units
        .slice(0, precision)           // limit to `precision` most significant
        .map(u => `${u.value}${u.label}`);

    return parts.length ? parts.join(" ") : "0ms";
}

export const LogStepsView: React.FC<{ steps: ITagLogStep[], limit?: number, shortFormat?: boolean }> = ({ steps, limit, shortFormat }) => {

    return <Timeline>
        {steps.slice().reverse().slice(0, limit).map((s, i) => (
            <Timeline.Item key={`${s.step}-${s.started_at}`} color={i === 0 ? "blue" : "green"}>
                <Space direction="vertical" size={shortFormat ? 0 : 4} style={{ width: "100%" }}>
                    <Space>
                        <strong>{s.step}</strong>
                        {!shortFormat && <>
                            ({moment(s.started_at).format("YYYY-MM-DD HH:mm:ss.SSS")})
                        </>}
                    </Space>

                    {(s.data && ((Array.isArray(s.data) && s.data.length > 0) || ( typeof s.data === "object" && Object.keys(s.data).length > 0))) && (
                        i === 0
                            ? <PrettyBlock data={s.data} />
                            : (shortFormat ? JSON.stringify(s.data) : <JsonBlock data={s.data} />)
                    )}
                </Space>
            </Timeline.Item>
        ))}
    </Timeline>
};


const NpiDashboardSyncTagImportLogs = (props: CardProps) => {

    const {loading, data:logs, run} = useRequest<ITagLog[], any>(npiApi.internal.dev.tagUpload.logs);
    const {loading: loadingDetail, data:log, run: runDetail} = useRequest<ITagLog, any>(npiApi.internal.dev.tagUpload.log, {manual: true});

    const [openId, setOpenId] = React.useState<number | null>(null);
    const isModalOpen = openId !== null;

    const openDetail = (id: number) => {
        setOpenId(id);
    };

    const closeDetail = () => {
        setOpenId(null);
    };

    useEffect(() => {
        if (openId !== null) {
            runDetail(openId);
        }
    }, [openId, runDetail]);

    // Auto-refresh every 3s while modal is open
    const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
    useEffect(() => {
        // clear previous poller
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }

        if (openId === null) return;

        if (isRunningImport(log)) {
            pollRef.current = setInterval(() => {
                runDetail(openId);
            }, 3000);
        }

        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [openId, log, runDetail]);


    const [filters, setFilters] = useState<string[]>([]);
    const [filterTypes, setFilterTypes] = useState<string[]>([]);
    const filterLogs = useMemo(() => {
        let filteredLogs = logs;
        if(filters.length > 0) {
            filteredLogs = filteredLogs.filter((log) => {
                return filters.includes(log.status);
            })
        }
        if(filterTypes.length > 0) {
            filteredLogs = filteredLogs.filter((log) => {
                return filterTypes.includes(log.type);
            })
        }

        return filteredLogs;
    }, [logs, filters, filterTypes])

    const toggleFilter = (status: string) => {
        const hasFilter = filters.find(f => f === status);
        if(hasFilter) {
            setFilters(filters.filter(f => f !== status));
        } else {
            setFilters([...filters, status]);
        }
    }
    const toggleFilterTypes = (type: string) => {
        const hasFilter = filterTypes.find(f => f === type);
        if(hasFilter) {
            setFilterTypes(filterTypes.filter(f => f !== type));
        } else {
            setFilterTypes([...filterTypes, type]);
        }
    }

    const hasFilter = useCallback((status: string) => {
        return filters.findIndex(f => f === status) >= 0;
    }, [filters]);
    const hasFilterType = useCallback((type: string) => {
        return filterTypes.findIndex(f => f === type) >= 0;
    }, [filterTypes]);


    const reset = () => {
        run();
    }


    return <>
        <Card loading={loading} {...props} title={<DevCardTitle title={'Tag Imports'} onReload={reset}/>}>

            <div style={{width: "100%", marginBottom: 10, gap: 10, display: "flex"}}>
                <Space.Compact style={{flex: 1}}>
                    <TagLogStatus disabled={!hasFilter("STARTED")} status={"STARTED"} onClick={() => toggleFilter("STARTED")}/>
                    <TagLogStatus disabled={!hasFilter("PENDING")} status={"PENDING"} onClick={() => toggleFilter("PENDING")}/>
                    <TagLogStatus disabled={!hasFilter("COMPLETED")} status={"COMPLETED"} onClick={() => toggleFilter("COMPLETED")}/>
                    <TagLogStatus disabled={!hasFilter("FAILED")} status={"FAILED"} onClick={() => toggleFilter("FAILED")}/>
                </Space.Compact>
            </div>

            <div style={{width: "100%", marginBottom: 10, gap: 10, display: "flex"}}>
                <Space.Compact style={{flex: 1}}>
                    <Tag color={!hasFilterType("FILE_IMPORT") ? "lightgray" : "blue"} onClick={() => toggleFilterTypes("FILE_IMPORT")}><FileExcelOutlined/> FILE IMPORT</Tag>
                    <Tag color={!hasFilterType("TAG_UPDATE_VALUE") ? "lightgray" : "blue"} onClick={() => toggleFilterTypes("TAG_UPDATE_VALUE")}><UserSwitchOutlined/> UPDATE</Tag>
                    <Tag color={!hasFilterType("TAG_REMOVE") ? "lightgray" : "blue"} onClick={() => toggleFilterTypes("TAG_REMOVE")}><UserDeleteOutlined/> REMOVE</Tag>
                </Space.Compact>
            </div>

            {!filterLogs || filterLogs.length === 0 ? (
                <div>No tags logs</div>
            ) : (
                <List
                    dataSource={filterLogs}
                    renderItem={(item) => (
                        <List.Item
                            onClick={() => openDetail(item.id)}
                            style={{ cursor: "pointer", flexDirection: "column", alignItems: "flex-start" }}
                        >
                            <Space>
                                <strong>#{item.id}</strong>
                                <Tooltip title={item.type}>
                                    {item.type === "FILE_IMPORT" ? <FileExcelOutlined/> : <UserSwitchOutlined/>}
                                </Tooltip>
                                {item.started_at}
                                {item?.status && <TagLogStatus status={item.status}/>}
                            </Space>
                            {item.original_filename && <em style={{marginTop: 5}}>
                                {item.original_filename}
                            </em>}
                        </List.Item>
                    )}
                />
            )}
        </Card>

        <Modal
            open={isModalOpen}
            onCancel={closeDetail}
            title={
                <Space>
                    <span>Log details</span>
                    {log?.status && <TagLogStatus status={log.status}/>}
                    <Button icon={<ReloadOutlined />} shape="circle" size={"small"} onClick={() => openId !== null && runDetail(openId)} disabled={loadingDetail}/>
                </Space>
            }
            width={900}
            destroyOnClose
            footer={null}
        >
            <Spin spinning={loadingDetail}>
                {log ? (
                    <>
                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="#log">{log.id}</Descriptions.Item>
                            <Descriptions.Item label="Type">{log.type}</Descriptions.Item>
                            <Descriptions.Item label="User ID">{log.user_id ?? '-'}</Descriptions.Item>
                            <Descriptions.Item label="Wave ID">{log.wave_id ?? '-'}</Descriptions.Item>
                            <Descriptions.Item label="Status">{log.status || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Started at">{log.started_at || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Finished at">{log.finished_at || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Duration (ms)">{log.duration_ms ? formatDuration(log.duration_ms, 4) : "-"}</Descriptions.Item>
                            {log.type === "FILE_IMPORT" && <>
                                <Descriptions.Item label="Original filename">{log.original_filename}</Descriptions.Item>
                                <Descriptions.Item label="File path" span={2}>{log.filepath || "-"}</Descriptions.Item>
                            </>}
                        </Descriptions>

                        <Collapse style={{ marginTop: 16 }} defaultActiveKey={log.status === "FAILED" ? ['error'] : ['steps']}>
                            {log.steps.length > 0 && <Collapse.Panel header="Steps" key="steps" >
                                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                    <Space style={{ marginBottom: 8 }}>
                                        <strong>Steps</strong>
                                    </Space>

                                    <LogStepsView steps={log.steps} />
                                </Space>
                            </Collapse.Panel>}

                            {log.error && <Collapse.Panel header="Error (JSON)" key="error">
                                <div dangerouslySetInnerHTML={{__html: log.error.message}}></div>
                                <hr/>
                                <JsonBlock data={log.error} />
                            </Collapse.Panel>}

                            {log.metas?.wave_ids && <Collapse.Panel header="Metas [Wave IDs]" key="metas_wave_ids">
                                <JsonBlock data={log.metas.wave_ids} whiteSpace={0}/>
                            </Collapse.Panel>}
                            {log.metas?.restricted_pos_ids && <Collapse.Panel header="Metas [Restrict by Pos IDS]" key="metas_restricted_pos_ids">
                                {log.metas.restricted_pos_ids && <JsonBlock data={log.metas.restricted_pos_ids} whiteSpace={0}/>}
                            </Collapse.Panel>}
                            {log.metas?.pos_ids && <Collapse.Panel header="Metas [All Pos IDS]" key="metas_pos_ids">
                                {log.metas.pos_ids && <JsonBlock data={log.metas.pos_ids} whiteSpace={0}/>}
                            </Collapse.Panel>}
                        </Collapse>
                    </>
                ) : (
                    <Typography.Text type="secondary">No data loaded yet.</Typography.Text>
                )}
            </Spin>
        </Modal>
    </>
};

export default NpiDashboardSyncTagImportLogs;